/* ==========================================================
   LIME LABS COMMERCE LAYER
   Component: Mexico Promotional Campaign Rail
   Project: La Troupe
   File: assets/ll-promo-mexico.js
   Version: 2.0.0
   Status: LLCL-QA

   Derived from assets/ll-promo.js v1.0.

   Owns only:
   - collapsed / expanded state
   - viewport motion triggers
   - manual open / close control
   - accessibility state
   - Shopify Theme Editor lifecycle
   - Mexico campaign countdown phase

   It is intentionally isolated from LLPromoManager so the
   legacy/current promotion and Mexico campaign can coexist
   in the Theme Editor without controlling each other.
   ========================================================== */

(() => {
  'use strict';

  const VERSION = '2.0.0';
  const MANAGER_KEY = 'LLPromoMexicoManager';
  const ROOT_SELECTOR = '[data-ll-promo-mexico]';
  const BAR_SELECTOR = '[data-ll-promo-mx-bar]';
  const DRAWER_SELECTOR = '[data-ll-promo-mx-drawer]';
  const TOGGLE_SELECTOR = '[data-ll-promo-mx-toggle]';
  const MANUAL_RELEASE_DISTANCE = 110;
  const FORCED_PREVIEW_MS = 8 * 60 * 60 * 1000;

  const previousManager = window[MANAGER_KEY];

  if (previousManager && previousManager.version === VERSION) {
    previousManager.scan(document);
    return;
  }

  if (previousManager && typeof previousManager.destroy === 'function') {
    previousManager.destroy();
  }

  const toNumber = (value, fallback) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const clamp = (value, minimum, maximum) =>
    Math.min(Math.max(value, minimum), maximum);

  const viewportHeight = () =>
    window.innerHeight || document.documentElement.clientHeight || 1;

  const pad = (value) => String(Math.max(0, value)).padStart(2, '0');

  class LLPromoMexicoController {
    constructor(root) {
      this.root = root;
      this.bar = root.querySelector(BAR_SELECTOR);
      this.drawer = root.querySelector(DRAWER_SELECTOR);
      this.toggle = root.querySelector(TOGGLE_SELECTOR);

      this.expanded = false;
      this.previewLock = false;
      this.manualOverride = false;
      this.manualStartY = window.scrollY;
      this.destroyed = false;
      this.rafPending = false;
      this.resizeTimer = null;
      this.countdownTimer = null;
      this.forcedPreviewTarget = null;
      this.phase = null;
      this.openLine = 64;
      this.closeLine = 18;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onToggle = this.onToggle.bind(this);

      if (!this.bar || !this.drawer) return;

      this.readSettings();
      this.init();
    }

    readSettings() {
      this.openLine = clamp(toNumber(this.root.dataset.openLine, 64), 40, 90);
      this.closeLine = clamp(toNumber(this.root.dataset.closeLine, 18), 0, 45);

      if (this.closeLine >= this.openLine - 12) {
        this.closeLine = Math.max(6, this.openLine - 38);
      }

      this.previewLock = this.root.classList.contains('is-preview-expanded');
    }

    init() {
      this.root.classList.add('is-js-ready');
      this.enableToggle();
      this.startCountdown();

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });

      if (this.previewLock) {
        this.setExpanded(true, 'editor-preview');
      } else {
        this.root.classList.remove('is-preview-expanded');
        this.setExpanded(false, 'initial');
        window.requestAnimationFrame(() => {
          if (!this.destroyed) this.syncFromGeometry('initial-geometry');
        });
      }
    }

    enableToggle() {
      if (!this.toggle) return;
      this.toggle.style.pointerEvents = 'auto';
      this.toggle.style.cursor = 'pointer';
      this.toggle.removeAttribute('aria-disabled');
      this.toggle.removeAttribute('tabindex');
      this.toggle.addEventListener('click', this.onToggle);
    }

    getCountdownTarget() {
      const forcePreview =
        this.root.dataset.designMode === 'true' &&
        this.root.dataset.countdownPreview === 'force';

      if (forcePreview) {
        if (!this.forcedPreviewTarget) {
          this.forcedPreviewTarget = Date.now() + FORCED_PREVIEW_MS;
        }
        return this.forcedPreviewTarget;
      }

      const parsed = Date.parse(this.root.dataset.launchAt || '');
      return Number.isFinite(parsed) ? parsed : null;
    }

    startCountdown() {
      window.clearInterval(this.countdownTimer);
      this.tickCountdown();

      if (this.phase === 'prelaunch') {
        this.countdownTimer = window.setInterval(() => {
          this.tickCountdown();
        }, 1000);
      }
    }

    tickCountdown() {
      const target = this.getCountdownTarget();

      if (!target) {
        this.setCampaignPhase('live');
        return;
      }

      const remaining = target - Date.now();

      if (remaining <= 0) {
        this.setCampaignPhase('live');
        window.clearInterval(this.countdownTimer);
        this.countdownTimer = null;
        return;
      }

      this.setCampaignPhase('prelaunch');

      const totalSeconds = Math.floor(remaining / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      this.root.querySelectorAll('[data-ll-countdown-days]').forEach((node) => {
        node.textContent = pad(days);
      });
      this.root.querySelectorAll('[data-ll-countdown-hours]').forEach((node) => {
        node.textContent = pad(hours);
      });
      this.root.querySelectorAll('[data-ll-countdown-minutes]').forEach((node) => {
        node.textContent = pad(minutes);
      });
      this.root.querySelectorAll('[data-ll-countdown-seconds]').forEach((node) => {
        node.textContent = pad(seconds);
      });

      const compact = days > 0
        ? `${pad(days)}D ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
        : `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;

      this.root.querySelectorAll('[data-ll-countdown-compact]').forEach((node) => {
        node.textContent = compact;
      });

      const timer = this.root.querySelector('[data-ll-countdown]');
      if (timer) {
        timer.setAttribute(
          'aria-label',
          `Faltan ${days} días, ${hours} horas, ${minutes} minutos y ${seconds} segundos para el inicio de la promoción.`
        );
      }
    }

    setCampaignPhase(phase) {
      if (phase !== 'prelaunch' && phase !== 'live') return;

      const changed = this.phase !== phase;
      this.phase = phase;
      this.root.classList.toggle('is-prelaunch', phase === 'prelaunch');
      this.root.classList.toggle('is-live', phase === 'live');
      this.root.dataset.campaignPhase = phase;

      if (changed) {
        this.root.dispatchEvent(
          new CustomEvent('ll:promo-mexico-phase', {
            bubbles: true,
            detail: {
              phase,
              campaignId: this.root.dataset.campaignId || ''
            }
          })
        );
      }
    }

    syncFromGeometry(source = 'geometry') {
      if (this.destroyed) return;

      if (this.previewLock) {
        this.setExpanded(true, 'editor-preview-lock');
        return;
      }

      if (this.manualOverride) return;

      const rect = this.bar.getBoundingClientRect();
      const height = viewportHeight();
      const probeY = rect.top + rect.height * 0.5;
      const openY = height * (this.openLine / 100);
      const closeY = height * (this.closeLine / 100);
      const insideMotionBand = probeY <= openY && probeY > closeY;

      this.setExpanded(insideMotionBand, source);
    }

    onScroll() {
      if (this.destroyed) return;

      if (this.manualOverride) {
        const distance = Math.abs(window.scrollY - this.manualStartY);
        if (distance >= MANUAL_RELEASE_DISTANCE) this.manualOverride = false;
      }

      if (this.rafPending) return;
      this.rafPending = true;

      window.requestAnimationFrame(() => {
        this.rafPending = false;
        this.syncFromGeometry('scroll');
      });
    }

    onResize() {
      window.clearTimeout(this.resizeTimer);
      this.resizeTimer = window.setTimeout(() => {
        if (this.destroyed) return;
        this.manualOverride = false;
        this.readSettings();
        this.syncFromGeometry('resize');
      }, 120);
    }

    setExpanded(open, source = 'unknown') {
      const next = Boolean(open);

      if (next === this.expanded) {
        this.syncAccessibility(next);
        return;
      }

      if (!next && document.activeElement && this.drawer.contains(document.activeElement)) {
        document.activeElement.blur();
      }

      this.expanded = next;
      this.root.classList.toggle('is-expanded', next);
      this.root.classList.toggle('is-collapsed', !next);

      if (!this.previewLock) this.root.classList.remove('is-preview-expanded');

      this.root.dataset.motionState = next ? 'expanded' : 'collapsed';
      this.syncAccessibility(next);

      this.root.dispatchEvent(
        new CustomEvent('ll:promo-mexico-state', {
          bubbles: true,
          detail: {
            state: next ? 'expanded' : 'collapsed',
            source,
            campaignId: this.root.dataset.campaignId || ''
          }
        })
      );
    }

    syncAccessibility(open) {
      this.drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
      this.drawer.inert = !open;
      if (this.toggle) this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    onToggle(event) {
      event.preventDefault();
      if (this.previewLock) return;

      this.manualOverride = true;
      this.manualStartY = window.scrollY;
      this.setExpanded(!this.expanded, 'manual-toggle');
    }

    refresh(source = 'refresh') {
      if (this.destroyed) return;

      this.readSettings();
      this.manualOverride = false;
      this.forcedPreviewTarget = null;
      this.startCountdown();

      if (this.previewLock) {
        this.setExpanded(true, `${source}-preview`);
      } else {
        this.root.classList.remove('is-preview-expanded');
        this.syncFromGeometry(source);
      }
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;

      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      window.clearTimeout(this.resizeTimer);
      window.clearInterval(this.countdownTimer);

      if (this.toggle) this.toggle.removeEventListener('click', this.onToggle);
    }
  }

  const controllers = new Map();

  const prune = () => {
    controllers.forEach((controller, root) => {
      if (root.isConnected) return;
      controller.destroy();
      controllers.delete(root);
    });
  };

  const initialize = (root) => {
    if (!root || controllers.has(root)) return;
    controllers.set(root, new LLPromoMexicoController(root));
  };

  const scan = (context = document) => {
    prune();
    if (context.matches?.(ROOT_SELECTOR)) initialize(context);
    context.querySelectorAll?.(ROOT_SELECTOR).forEach(initialize);
  };

  const findRoot = (target) => {
    if (!target) return null;
    if (target.matches?.(ROOT_SELECTOR)) return target;
    const closest = target.closest?.(ROOT_SELECTOR);
    if (closest) return closest;
    return target.querySelector?.(ROOT_SELECTOR) || null;
  };

  const destroyWithin = (target) => {
    controllers.forEach((controller, root) => {
      const belongs = root === target || (target?.contains && target.contains(root));
      if (!belongs) return;
      controller.destroy();
      controllers.delete(root);
    });
  };

  const onSectionLoad = (event) => {
    scan(event.target);
    window.requestAnimationFrame(() => {
      const root = findRoot(event.target);
      controllers.get(root)?.refresh('section-load');
    });
  };

  const onSectionUnload = (event) => destroyWithin(event.target);

  const onSectionSelect = (event) => {
    const root = findRoot(event.target);
    controllers.get(root)?.refresh('section-select');
  };

  const onSectionDeselect = (event) => {
    const root = findRoot(event.target);
    controllers.get(root)?.refresh('section-deselect');
  };

  const onSectionReorder = () => {
    window.requestAnimationFrame(() => {
      controllers.forEach((controller) => controller.refresh('section-reorder'));
    });
  };

  document.addEventListener('shopify:section:load', onSectionLoad);
  document.addEventListener('shopify:section:unload', onSectionUnload);
  document.addEventListener('shopify:section:select', onSectionSelect);
  document.addEventListener('shopify:section:deselect', onSectionDeselect);
  document.addEventListener('shopify:section:reorder', onSectionReorder);

  const manager = {
    version: VERSION,
    scan,
    refresh() {
      scan(document);
      controllers.forEach((controller) => controller.refresh('manager-refresh'));
    },
    open() {
      controllers.forEach((controller) => {
        controller.previewLock = false;
        controller.manualOverride = true;
        controller.manualStartY = window.scrollY;
        controller.setExpanded(true, 'manager-open');
      });
    },
    close() {
      controllers.forEach((controller) => {
        controller.previewLock = false;
        controller.manualOverride = true;
        controller.manualStartY = window.scrollY;
        controller.setExpanded(false, 'manager-close');
      });
    },
    status() {
      const rows = [];
      controllers.forEach((controller) => {
        rows.push({
          version: VERSION,
          campaign: controller.root.dataset.campaignId || '',
          state: controller.expanded ? 'expanded' : 'collapsed',
          phase: controller.phase,
          launchAt: controller.root.dataset.launchAt || '',
          preview: controller.previewLock,
          openLine: controller.openLine,
          closeLine: controller.closeLine
        });
      });
      console.table(rows);
      return rows;
    },
    destroy() {
      controllers.forEach((controller) => controller.destroy());
      controllers.clear();
      document.removeEventListener('shopify:section:load', onSectionLoad);
      document.removeEventListener('shopify:section:unload', onSectionUnload);
      document.removeEventListener('shopify:section:select', onSectionSelect);
      document.removeEventListener('shopify:section:deselect', onSectionDeselect);
      document.removeEventListener('shopify:section:reorder', onSectionReorder);
    }
  };

  window[MANAGER_KEY] = manager;
  window.LLPromoMexico = manager;

  const start = () => scan(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
